import { useEffect, useState } from "react";
import { PencilIcon, PlusIcon } from "lucide-react";
import ChangelogModal from "./ChangelogModal";
import type { ChangeLogType } from "../../../../src/types";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Switch } from "../ui/switch";
import Pagination from "../ui/pagination";
import { Button } from "../ui/button";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "../ui/table";
export default function Changelogs() {
  const { toast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingChangelog, setEditingChangelog] =
    useState<ChangeLogType | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [changeLogs, setChangeLogs] = useState<ChangeLogType[]>([]);

  useEffect(() => {
    const fetchChangeLogs = async () => {
      const changeLogs = await api.getChangeLogs(page, perPage);
      if (changeLogs.message) {
        toast({
          title: "Error",
          description: changeLogs.message,
          variant: "destructive",
        });
      } else {
        setChangeLogs(changeLogs.changeLogs);
        setTotalPages(changeLogs.totalPages);
      }
    };
    fetchChangeLogs();
  }, []);

  const handleCreateNew = () => {
    setEditingChangelog(null);
    setIsModalOpen(true);
  };

  const handleEdit = (changelog: ChangeLogType) => {
    setEditingChangelog(changelog);
    setIsModalOpen(true);
  };

  const updateChangelogStatus = async (id: number, isActive: boolean) => {
    await api.updateChangeLog({ id, is_active: isActive });
    setChangeLogs(prevChangeLogs =>
      prevChangeLogs.map(changelog =>
        changelog.id === id ? { ...changelog, is_active: isActive } : changelog,
      ),
    );
    toast({
      title: "Success",
      description: `Changelog for version ${
        changeLogs.find(changelog => changelog.id === id)?.version
      } status updated`,
    });
  };

  const saveChangelog = async () => {
    const changeLogsData = await api.getChangeLogs(page, perPage);
    if (changeLogsData.message) {
      toast({
        title: "Error",
        description: changeLogsData.message,
        variant: "destructive",
      });
    } else {
      setChangeLogs(changeLogsData.changeLogs);
      setTotalPages(changeLogsData.totalPages);
    }
    setIsModalOpen(false);
    setEditingChangelog(null);
    toast({
      title: "Success",
      description: "Changelog saved",
    });
  };

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            Changelogs
          </h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-white">
            Manage application changelog entries
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <Button
            type="button"
            onClick={handleCreateNew}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-gray-900 dark:bg-white px-4 py-2 text-sm font-medium text-white dark:text-gray-900 shadow-sm hover:bg-green-700 dark:hover:bg-green-700"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            New Changelog
          </Button>
        </div>
      </div>

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <Table className="w-full bg-white dark:bg-gray-900">
                <TableHeader>
                  <TableRow>
                    <TableHead
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white"
                    >
                      Version
                    </TableHead>
                    <TableHead
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white"
                    >
                      Release Date
                    </TableHead>
                    <TableHead
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-white"
                    >
                      Status
                    </TableHead>
                    <TableHead
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-6 text-gray-900 dark:text-white"
                    >
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="divide-y divide-gray-200 bg-white dark:bg-gray-900">
                  {changeLogs.map(changelog => (
                    <TableRow key={changelog.id}>
                      <TableCell className="px-3 py-3.5 text-gray-900 dark:text-white">
                        {changelog.version}
                      </TableCell>
                      <TableCell className="px-3 py-3.5 text-gray-900 dark:text-white">
                        {changelog.release_date}
                      </TableCell>
                      <TableCell className="px-3 py-3.5 text-gray-900 dark:text-white">
                        <Switch
                          checked={changelog.is_active}
                          onCheckedChange={() => {
                            updateChangelogStatus(
                              changelog.id,
                              !changelog.is_active,
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell className="px-3 py-3.5 text-gray-900 dark:text-white">
                        <Button
                          type="button"
                          onClick={() => handleEdit(changelog)}
                          className="text-white dark:text-gray-900 hover:text-gray-400 dark:hover:text-gray-700"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      {isModalOpen && (
        <ChangelogModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          changelog={editingChangelog}
          onSave={() => saveChangelog()}
        />
      )}
    </div>
  );
}
